<script lang="ts">
    import Component, { mixins } from 'vue-class-component';
    import PortfolioNavigation from "@common/Components/PortfolioNavigation.vue";
    import { AlternativesListReadModel, StatusDependenciesReadModel } from '@Alternatives/AlternativesLists/Domain/readmodels';
    import AlternativesListsQueries from '@Alternatives/AlternativesLists/Domain/queries-vue-component';
    import AlternativesListsCommands from '@Alternatives/AlternativesLists/Domain/commands-vue-component';
    import { DeleteKanbanStatusCommandV1, UpdateKanbanStatusesCommandV1 } from '@Alternatives/AlternativesLists/Domain/commands-core';
    import { generateName, isNullOrWhitespace, numCompare } from '@common/Common';
    import { AlternativeStatusReadModel } from '@Alternatives/Statuses/Domain/readmodels';
    import { AlternativeStatusDTO } from '@Alternatives/Model';
    import { Refresh } from '@browser/Browser';
    import { ValidationError } from '@common/types';
    import { ICommandBase } from '@Alternatives/cqrs';
    import { ICommandStatus } from '@cqrs/cqrs';

    @Component({
        components: {
            PortfolioNavigation
        }
    })
    export default class ManageKanbanStatusesPage extends mixins(AlternativesListsQueries, AlternativesListsCommands)
    {
        get workspaceId(): number { return Number.parseInt(this.$route.params.workspaceId); }
        get projectId(): number { return Number.parseInt(this.$route.params.projectId); }

        rm: AlternativesListReadModel = null;
        depViewer: StatusDependenciesViewerViewModel = new StatusDependenciesViewerViewModel();
        statusDeleter: StatusDeleterViewModel = new StatusDeleterViewModel();
        errors: ValidationError[] = null;

        get AlternativeStatusesView(): AlternativeStatusReadModel[]  { return this.rm.Statuses.sort((a, b) => numCompare(a.OrderValue, b.OrderValue)); }
        get EmptyNameExist(): boolean { return this.rm.Statuses.some(a => isNullOrWhitespace(a.Name)); }
        get SaveBtnEnable(): boolean { return this.EmptyNameExist === false && this.rm.Statuses.length > 0; }
        get First(): AlternativeStatusReadModel { return this.AlternativeStatusesView[0]; }
        get Last(): AlternativeStatusReadModel { return this.AlternativeStatusesView[this.AlternativeStatusesView.length - 1]; }

        
        createStatus()
        {
            const status: AlternativeStatusReadModel = new AlternativeStatusReadModel();
            status.Id = 0;
            status.Name = generateName("Status");
            status.OrderValue = this.Last.OrderValue + 1;
            this.rm.Statuses.push(status);
        }
        
        deleteStatus(s: AlternativeStatusReadModel)
        {
            if (s.Id == 0)
            {
                this.rm.Statuses.remove(s);
            }
            else
            {
                this.statusDeleter.opened = true;
                this.runStatusDependenciesQueryV1(this.workspaceId, this.projectId, this.rm.Id, s.Id)
            }
        }

        moveUp(s: AlternativeStatusReadModel)
        {
            if (s.Id === this.First.Id) return;

            const thisIndex = this.AlternativeStatusesView.indexOf(s);
            const prevStatusVM = this.AlternativeStatusesView[thisIndex - 1];

            if (prevStatusVM != null)
            {
                prevStatusVM.OrderValue = s.OrderValue;
                s.OrderValue--;
            }
        }

        moveDown(s: AlternativeStatusReadModel)
        {
            if (s.Id === this.Last.Id) return;

            let thisIndex = this.AlternativeStatusesView.indexOf(s);
            let nextStatusVM = this.AlternativeStatusesView[thisIndex + 1];

            if (nextStatusVM != null)
            {
                nextStatusVM.OrderValue = s.OrderValue;
                s.OrderValue++;
            }
        }

        showStatusDependenciesViewer(statusId: number)
        {
            if (statusId > 0)
            {
                if (this.statusDeleter.opened === true)
                {
                    this.statusDeleter.opened = false;
                }
                else
                {
                    this.runStatusDependenciesQueryV1(this.workspaceId, this.projectId, this.rm.Id, statusId);
                }

                this.depViewer.opened = true;
            }
        }




        handleUpdateKanbanStatusesCommand()
        {
            const statuses: AlternativeStatusDTO[] = this.rm.Statuses.map(s =>
            {
                return {
                    Id: s.Id,
                    Name: s.Name,
                    OrderValue: s.OrderValue
                };
            });
            this.invokeUpdateKanbanStatusesCommandV1(this.workspaceId, this.rm.Id, statuses);
        }

        handleDeleteKanbanStatusCommand()
        {
            this.invokeDeleteKanbanStatusCommandV1(this.workspaceId, this.projectId, this.rm.Id, this.statusDeleter.rm.Id);
        }

        
        
        mounted()
        {   
            document.title = "Kanban statuses | TransparentChoice";
            this.runAlternativesListByProjectIdQueryV1(this.workspaceId, this.projectId);
        }

        onSetModel(model: any, modelName: string) 
        {
            if (modelName == "AlternativesListReadModel")
            {
                this.rm = model as AlternativesListReadModel;
            }
            else if (modelName == "StatusDependenciesReadModel")
            {
                const rm = model as StatusDependenciesReadModel;
                this.depViewer.rm = rm;
                this.statusDeleter.rm = rm;
            }
        }

        onCommandSucceed(cmd: ICommandBase, correlationId: string, result: any)
        {
            this.errors = null;
            
            if (UpdateKanbanStatusesCommandV1.matchType(cmd))
            {
                Refresh();
            }
            else if (DeleteKanbanStatusCommandV1.matchType(cmd))
            {
                this.statusDeleter.close();
                this.rm.Statuses = this.rm.Statuses.filter(a => a.Id !== Number(correlationId));
            }
        }

        onCommandFailed(status: ICommandStatus)
        {
            if (UpdateKanbanStatusesCommandV1.matchType(status.Command))
            {
                this.errors = status.ValidationErrors;
            }
        }
    }

    class StatusDependenciesViewerViewModel
    {
        opened: boolean = false;
        rm: StatusDependenciesReadModel = null;

        close()
        {
            this.opened = false;
            this.rm = null;
        }
    }

    class StatusDeleterViewModel
    {
        opened: boolean = false;
        rm: StatusDependenciesReadModel = null;

        close()
        {
            this.opened = false;
            this.rm = null;
        }
    }

</script>

<template>
    <div>
        <div id="StatusDependenciesViewerDialog" class="modal-mask" v-if="depViewer.opened === true">
            <div class="modal-wrapper">
                <div class="modal-dialog modal-xl modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header">
                            <h5 class="modal-title">Status dependencies</h5>
                            <button type="button" class="close" @click="depViewer.close()">&times;</button>
                        </div>

                        <div class="modal-body">
                            <h4 v-if="!depViewer.rm">
                                <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                            </h4>
                            <div v-else>
                                <div class="alert alert-warning text-dark" role="alert">
                                    <p>The status '<strong>{{depViewer.rm.Name}}</strong>' is used in <strong>{{depViewer.rm.Count}}</strong> places.</p>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Reports.length > 0">
                                    <div class="card-header">
                                        Reports ({{depViewer.rm.Reports.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="r in depViewer.rm.Reports" :key="r.Id">
                                            <a class="text-primary" target="_blank" v-sanitizedHref="r.Url">{{r.Name}}</a>
                                        </li>
                                    </ul>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Lists.length > 0">
                                    <div class="card-header">
                                        Lists ({{depViewer.rm.Lists.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="l in depViewer.rm.Lists" :key="l.Id">
                                            <span>{{l.Name}}</span>
                                        </li>
                                    </ul>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Notifications.length > 0">
                                    <div class="card-header">
                                        Notifications ({{depViewer.rm.Notifications.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="n in depViewer.rm.Notifications" :key="n.Id">
                                            <span>{{n.Name}}</span>
                                        </li>
                                    </ul>
                                </div>

                                <div class="card mt-3" v-if="depViewer.rm.Alternatives.length > 0">
                                    <div class="card-header">
                                        Alternatives ({{depViewer.rm.Alternatives.length}})
                                    </div>
                                    <ul class="list-group list-group-flush">
                                        <li class="list-group-item" v-for="a in depViewer.rm.Alternatives" :key="a.Id">
                                            <span>{{a.Name}}</span>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div class="modal-footer">
                            <button @click="depViewer.close()" type="button" class="btn btn-outline-secondary">Close</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div if="StatusDeleterDialog" class="modal-mask" v-if="statusDeleter.opened === true">
            <div class="modal-wrapper">
                <div class="modal-dialog modal-lg modal-dialog-scrollable">
                    <div class="modal-content">
                        <div class="modal-header" :class="{'bg-danger': (statusDeleter.rm && statusDeleter.rm.HasDependants === false)}">
                            <h5 class="modal-title">Delete status</h5>
                            <button type="button" class="close" @click="statusDeleter.close()">&times;</button>
                        </div>

                        <div class="modal-body">
                            <h4 v-if="!statusDeleter.rm">
                                <i class="fa fa-spinner fa-spin"></i> Checking dependencies...
                            </h4>
                            <div v-else>
                                <div v-if="statusDeleter.rm.HasDependants === true">
                                    <div class="alert alert-danger text-dark" role="alert">
                                        <h5 class="alert-heading">Cannot delete this status</h5>
                                        <p>
                                            The status '<strong>{{statusDeleter.rm.Name}}</strong>' is used in <strong>{{statusDeleter.rm.Count}}</strong> places.
                                            To delete this status, first remove it from where it is used.
                                        </p>
                                    </div>
                                </div>
                                <div v-else>
                                    <h4>Are you sure you want to delete this status?</h4>
                                    <p>You are about to delete '<strong>{{statusDeleter.rm.Name}}</strong>'. This can't be undone.</p>
                                </div>

                            </div>
                        </div>

                        <div class="modal-footer">
                            <button v-if="statusDeleter.rm?.HasDependants === false" type="button" class="btn btn-danger" @click="handleDeleteKanbanStatusCommand">Delete</button>
                            <button v-if="statusDeleter.rm?.HasDependants === true" type="button" class="btn btn-secondary" @click="showStatusDependenciesViewer(statusDeleter.rm.Id)">View dependencies</button>
                            <button type="button" class="btn btn-outline-secondary" @click="statusDeleter.close()">Cancel</button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <PortfolioNavigation :workspaceId="workspaceId" :projectId="projectId" />

        <div class="container" style="margin-top: 80px; padding-top: 40px;">
            <div class="row">
                <div class="col-lg-6">
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Kanban statuses</h1>
                </div>
                <div class="col-lg-6 text-right">
                    <button type="button" class="btn btn-secondary" style="padding: 0.7em 2em;" @click="createStatus">Add status</button>
                    <button type="button" class="btn btn-success" style="padding: 0.7em 2em;" @click="handleUpdateKanbanStatusesCommand">Save changes</button>
                </div>
            </div>
        </div>

        <hr style="border-color: #bbb; margin: 20px 0 60px;" />

        <div class="container" v-if="rm">
            <div class="alert alert-danger" role="alert" v-if="AlternativeStatusesView.length === 0">
                <strong>No statuses</strong> - there must be at least one status.
            </div>

            <div class="alert alert-danger" role="alert" v-if="errors?.length">
                <p v-for="e in errors">
                    {{e.Error}}
                </p>
            </div>

            <table class="table table-bordered">

                <tr v-for="s in AlternativeStatusesView" :key="s.OrderValue">
                    <td style="width: 120px;">
                        <button type="button" @click="moveUp(s)" class="btn btn-outline-secondary" style="max-width: 40px; width: 40px;">
                            <i class="fa fa-long-arrow-up" aria-hidden="true"></i>
                        </button>
                        <button type="button" @click="moveDown(s)" class="btn btn-outline-secondary" style="max-width: 40px; width: 40px;">
                            <i class="fa fa-long-arrow-down" aria-hidden="true"></i>
                        </button>
                    </td>
                    <td>
                        <div class="form-group m-0 p-0">
                            <input type="text" v-model.trim="s.Name" class="form-control">
                        </div>
                    </td>
                    <td style="width: 90px;">
                        <div class="dropdown">
                            <button type="button" class="btn btn-outline-secondary dropdown-toggle" data-toggle="dropdown">
                                Actions
                            </button>
                            <div class="dropdown-menu dropdown-menu-right">
                                <a class="dropdown-item" v-if="s.Id" @click="showStatusDependenciesViewer(s.Id)">View dependencies</a>
                                <a class="dropdown-item" @click="deleteStatus(s)">Delete</a>
                            </div>
                        </div>
                    </td>
                </tr>

            </table>


        </div>


    </div>
</template>

<style scoped>

</style>


